import React from "react";
import { isErrorEventType } from "NeoAnalytics/executionSteps";

export type ScatterEvent = {
  description: string;
  type: string;
  time: number;
  label: string;
  parentEventId?: string;
  value?: unknown;
  color?: string;
  mergedCount?: number;
  mergedItems?: ScatterEvent[];
};

export type TypeMeta = {
  color: string;
  label?: string;
};

const DEFAULT_TYPE_META: Record<string, TypeMeta> = {
    ERROR: { color: "#B42318", label: "Error" },
    INTERACTION: { color: "#5B3CC4", label: "Interaction" },
    PROCEDURE: { color: "#14B8D4", label: "Procedure" },
    SPEECH_RECOGNITION: { color: '#13d827ff', label: 'Speech' },
};

// Lighter variants for use on dark backgrounds
const DARK_TYPE_META: Record<string, TypeMeta> = {
    ERROR: { color: "#ff6b6b", label: "Error" },
    INTERACTION: { color: "#a78bfa", label: "Interaction" },
    PROCEDURE: { color: "#38bdf8", label: "Procedure" },
    SPEECH_RECOGNITION: { color: '#4ade80', label: 'Speech' },
};

export function resolveTypeMeta(
    type: string,
    typeMeta?: Record<string, TypeMeta>,
    fallback?: TypeMeta,
    isDark?: boolean
): TypeMeta {
    const defaults = isDark ? DARK_TYPE_META : DEFAULT_TYPE_META;

    if (isErrorEventType(type)) {
        return typeMeta?.ERROR ?? defaults.ERROR;
    }

    if (type?.toUpperCase().includes('WARNING')) {
        return typeMeta?.WARNING ?? (isDark ? { color: "#fbbf24", label: "Warning" } : { color: "#F59E0B", label: "Warning" });
    }

    if (type?.toUpperCase().includes('INFO')) {
        return typeMeta?.INFO ?? (isDark ? { color: "#60a5fa", label: "Info" } : { color: "#2563EB", label: "Info" });
    }

    if (type?.toUpperCase().includes('PROCEDURE')) {
        return typeMeta?.PROCEDURE ?? defaults.PROCEDURE;
    }

    if (type?.toUpperCase().includes('INTERACTION')) {
        return typeMeta?.INTERACTION ?? defaults.INTERACTION;
    }

    return (
        typeMeta?.[type] ??
    defaults[type] ??
    DEFAULT_TYPE_META[type] ??
    fallback ??
    (isDark ? { color: "#94a3b8", label: type } : { color: "#6B7280", label: type })
    );
}

type ShapeProps = {
  cx?: number;
  cy?: number;
  payload?: ScatterEvent;
};

export type EventScatterShapeProps = ShapeProps & {
  typeMeta?: Record<string, TypeMeta>;
  fallbackTypeMeta?: TypeMeta;

  size?: number;
  radius?: number;
  isActive?: boolean;
  onSelect?: (event: ScatterEvent, point: { cx: number; cy: number }) => void;
};

export function EventScatterShape({
    cx = 0,
    cy = 0,
    payload,
    typeMeta,
    fallbackTypeMeta,
    size = 6,
    radius = 3,
    isActive = false,
    onSelect,
}: EventScatterShapeProps) {
    if (cx == null || cy == null || !payload) {return null;}

    const resolvedMeta = resolveTypeMeta(payload.type, typeMeta, fallbackTypeMeta);
    const meta = payload.color ? { ...resolvedMeta, color: payload.color } : resolvedMeta;
    const isMerged = (payload.mergedCount ?? 1) > 1;
    const mergedSize = isMerged ? size + 4 : size;
    const mergedRadius = isMerged ? Math.min(radius + 1, 6) : radius;

    return (
        <g
            style={{
                cursor: onSelect ? 'pointer' : 'default',
                transformBox: 'fill-box',
                transformOrigin: 'center',
                transform: isActive ? 'scale(1.14)' : 'scale(1)',
                transition: 'transform 160ms ease-out',
            }}
            onClick={() => onSelect?.(payload, { cx, cy })}
        >
            {isMerged && (
                <rect
                    x={cx - mergedSize / 2 - 2}
                    y={cy - mergedSize / 2 - 2}
                    width={mergedSize + 4}
                    height={mergedSize + 4}
                    rx={mergedRadius + 2}
                    ry={mergedRadius + 2}
                    fill="rgba(255,255,255,0.92)"
                    stroke={meta.color}
                    strokeWidth={1.5}
                    strokeDasharray="3 2"
                />
            )}
            <rect
                x={cx - mergedSize / 2}
                y={cy - mergedSize / 2}
                width={mergedSize}
                height={mergedSize}
                rx={mergedRadius}
                ry={mergedRadius}
                fill={meta.color}
                stroke="rgba(255, 255, 255, 1)"
                strokeWidth={1}
            />
            {isMerged && (
                <text
                    x={cx}
                    y={cy + 0.5}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="#ffffff"
                    fontSize={Math.max(9, mergedSize * 0.48)}
                    fontWeight={700}
                >
                    {payload.mergedCount}
                </text>
            )}
        </g>
    );
}

export function EventScatterActiveShape(props: EventScatterShapeProps) {
    return <EventScatterShape {...props} isActive size={props.size ?? 18} radius={props.radius ?? 4} />;
}
